import AdSpendInput from '_components/AdSpendInput';
import axios from 'axios';
import cn from 'classnames';
import { GatsbyImage } from 'gatsby-plugin-image';
import { useContentfulImage } from 'gatsby-source-contentful/hooks';
import React, { useMemo, useState } from 'react';
import { formSubmitTrack } from 'src/utils/analytics';
import { LABEL, PAID_SOCIAL } from 'src/utils/analytics/constants';
import { Hubspot } from '../../../constants/destinations';
import { BASE_ENDPOINTS } from '../../../constants/endpoints';
import DropDownIcon from '../../../images/drop-down.svg';
import { sendToHubspot } from '../../../services/hubspot';
import { ANALYTICS_FIELDS } from '../../../utils/analytics/hiddenFields';
import { getCountriesList } from '../../../utils/countries';
import { validateEmail } from '../../../utils/email';
import { prepareURLWithCORS } from '../../../utils/proxyRequest';
import Button, { ButtonSize, ButtonVariant } from '../../Button';
import CountryInput from '../../CountryInput';
import FormErrorMessage from '../../FormErrorMessage';
import Loading from '../../Loading';
import TermsAndConditions from '../../TermsAndConditions';

interface WebinarsRegisterFormProps {
  buttonText?: string;
  successText?: string;
  formId?: string;
  pageTitle?: string;
  eventId?: string;
  setActiveModal: React.Dispatch<React.SetStateAction<boolean>>;
  termsAndConditions: string;
}

const source_of_webinar_information = [
  'Social Media (LinkedIn, Twitter, Facebook, Instagram)',
  'Email',
  'Perpetua Contact',
  'Other',
];

const WebinarsRegisterForm: React.FC<WebinarsRegisterFormProps> = props => {
  const {
    successText = 'Thank You!',
    buttonText = 'Register',
    formId,
    pageTitle,
    eventId,
    setActiveModal,
    termsAndConditions,
  } = props;

  const [userInfo, setUserInfo] = useState<{
    email: string;
    firstName: string;
    lastName: string;
    company: string;
    webinarInformation: string;
  }>({
    email: '',
    firstName: '',
    lastName: '',
    company: '',
    webinarInformation: '',
  });
  const [submitted, setSubmitted] = React.useState<boolean>(false);
  const [loading, setLoading] = React.useState<boolean>(false);
  const [inputError, setInputError] = React.useState<boolean>(false);
  const [countries, setCountries] = useState<
    {
      name: string;
      code: string;
    }[]
  >([]);
  const [location, setLocation] = useState<{
    name?: string;
    code?: string;
  }>({ name: '', code: '' });
  const [webinarInformation, setWebinarInformation] = useState<boolean>(false);
  const [adSpend, setAdSpend] = useState<{
    title?: string;
    hsID?: string;
  }>({ title: '', hsID: '' });

  useMemo(() => {
    const fetchCountries = async () => {
      const countriesList = await getCountriesList();
      setCountries(countriesList);
    };
    fetchCountries();
  }, []);

  const isValidForm = () => {
    return (
      userInfo.firstName !== '' &&
      userInfo.lastName !== '' &&
      validateEmail(userInfo.email) &&
      userInfo.company !== '' &&
      userInfo.webinarInformation !== '' &&
      location.name !== ''
    );
  };

  const clearForm = () => {
    setUserInfo({
      email: '',
      firstName: '',
      lastName: '',
      company: '',
      webinarInformation: '',
    });
    setLocation({ name: '', code: '' });
  };

  const submitToHubSpot = async () => {
    const portalId = Hubspot.portalId;
    const formGuid = Hubspot.WEBINAR_FORM_ID;

    const postData = {
      portalId,
      formGuid,
      fields: [
        {
          name: 'firstname',
          value: userInfo.firstName,
        },
        {
          name: 'lastname',
          value: userInfo.lastName,
        },
        {
          name: 'email',
          value: userInfo.email,
        },
        {
          name: 'company',
          value: userInfo.company,
        },
        {
          name: 'country',
          value: location?.name,
        },
        {
          name: 'how_did_you_hear_abo',
          value: userInfo.webinarInformation,
        },
        {
          name: 'perpetua_ad_spend_tier',
          value: adSpend?.hsID,
        },
        {
          name: 'webinar_title',
          value: pageTitle,
        },
        ...ANALYTICS_FIELDS(),
      ],
    };

    const { status } = await sendToHubspot(postData);
    if (status === 200) {
      // no ph
      formSubmitTrack({
        label: `Live ${LABEL.Webinar}`,
        adSpend: adSpend?.hsID,
        paidSocialEvents: PAID_SOCIAL.webinars,
        facebookPayload: {
          em: userInfo.email,
          fn: userInfo.firstName,
          ln: userInfo.lastName,
          country: location?.name,
        },
      });

      clearForm();
      setLoading(false);
      setSubmitted(true);
    } else {
      console.log(status);
    }
  };

  const buttonOnClick = () => {
    if (isValidForm()) {
      setLoading(true);
      submitToHubSpot();
      registerToBizzaboEvent();
      setTimeout(() => {
        setSubmitted(false);
        setActiveModal(false);
      }, 5000);
    } else {
      setInputError(true);
    }
  };

  const sharedInputClasses =
    'newsletter-signup-input border border-gray-3 rounded-lg focus:border-indigo-1 focus:outline-none focus:ring-4 focus:ring-indigo-3-light py-2 px-4 w-full text-base mt-2';

  const sharedLabelClasses = 'text-base font-semibold';

  const config = {
    headers: {
      Accept: 'application/vnd.bizzabo.v2.0+json',
      Authorization: 'Bearer 2a23ea4d-bf98-41a5-9265-51eb2bbd8c4b',
      'Content-Type': 'application/json',
    },
  };
  const postData = {
    properties: {
      firstName: userInfo.firstName,
      lastName: userInfo.lastName,
      email: userInfo.email,
      how_did_you_hear_abo: userInfo.webinarInformation,
      company: userInfo.company,
      country: location?.name,
    },
    ticketId: formId,
    paymentMethod: 'check',
  };

  const registerToBizzaboEvent = () => {
    const endpoint = prepareURLWithCORS(
      `${BASE_ENDPOINTS.BIZZABO}/registrations?eventId=${eventId}`
    );
    axios
      .post(endpoint, postData, config)
      .then(res => setSubmitted(true))
      .catch(err => {
        console.log(err);
      });
  };

  const thankYouImage = useContentfulImage({
    image: {
      url: '//images.ctfassets.net/2c85gj41xf35/5ewf6xZnHRGoNGJ2fTXjOL/7aba2318273cc4a4ef0d5a6084778977/thankYouBizzabo.png',
      width: 88,
      height: 88,
      quality: 100,
    },
  });

  return (
    <div aria-hidden={true} className="w-full mx-auto mt-10 overflow-y-scroll">
      <div className="max-w-full mx-auto px-16">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          <div>
            <label className={sharedLabelClasses}>First Name</label>
            <input
              className={cn(
                sharedInputClasses,
                inputError && userInfo.firstName.length <= 0 && 'input-error'
              )}
              placeholder={'First Name'}
              value={userInfo?.firstName}
              onChange={e =>
                setUserInfo({ ...userInfo, firstName: e.target.value })
              }
            />
          </div>
          <div>
            <label className={sharedLabelClasses}>Last Name</label>
            <input
              className={cn(
                sharedInputClasses,
                inputError && userInfo.lastName.length <= 0 && 'input-error'
              )}
              placeholder={'Last Name'}
              value={userInfo?.lastName}
              onChange={e =>
                setUserInfo({ ...userInfo, lastName: e.target.value })
              }
            />
          </div>
          <div>
            <label className={sharedLabelClasses}>Work Email</label>
            <input
              className={cn(
                sharedInputClasses,
                inputError && !validateEmail(userInfo?.email) && 'input-error'
              )}
              placeholder={'Work Email'}
              value={userInfo?.email}
              onChange={e =>
                setUserInfo({ ...userInfo, email: e.target.value })
              }
            />
          </div>
          <div>
            <label className={sharedLabelClasses}>Company</label>
            <input
              className={cn(
                sharedInputClasses,
                inputError && userInfo.company.length <= 0 && 'input-error'
              )}
              placeholder={'Company'}
              value={userInfo?.company}
              onChange={e =>
                setUserInfo({ ...userInfo, company: e.target.value })
              }
            />
          </div>
          <div className="w-full">
            <label className={cn(sharedLabelClasses, 'mb-2 block')}>
              Country
            </label>
            <CountryInput
              countries={countries}
              inputError={inputError}
              location={location}
              setLocation={setLocation}
            />
          </div>
          <div>
            <label className={sharedLabelClasses}>
              How did you hear about the webinar?
            </label>
            <Button
              onClick={() => {
                setWebinarInformation(!webinarInformation);
              }}
              className={cn(
                'bg-white flex justify-between border border-gray-3 rounded-lg focus:border-indigo-1 focus:outline-none focus:ring-4 focus:ring-indigo-3-light py-2 px-4 w-full text-base mt-2  h-10',
                inputError &&
                  userInfo.webinarInformation.length <= 0 &&
                  'input-error'
              )}
            >
              <div>
                {userInfo?.webinarInformation ? (
                  <span>{userInfo?.webinarInformation}</span>
                ) : (
                  <span className="text-gray-3 font-medium">- Select -</span>
                )}
              </div>
              <DropDownIcon />
              {webinarInformation === true && (
                <ul className="z-10 absolute top-full mt-1 py-2 left-0 bg-white overflow-hidden w-full rounded-md shadow-2xl">
                  {source_of_webinar_information.map((key, index) => (
                    <>
                      <li
                        key={index}
                        onClick={e =>
                          setUserInfo({ ...userInfo, webinarInformation: key })
                        }
                        className="w-full flex items-center justify-between realative text-left px-6 py-2 text-base hover:bg-gray-5-light"
                      >
                        {key}
                      </li>
                    </>
                  ))}
                </ul>
              )}
            </Button>
          </div>
          <div className="w-full">
            <label className={`mb-2 block ${sharedLabelClasses}`}>
              What is your current monthly ad spend?
            </label>
            <AdSpendInput
              adSpend={adSpend}
              setAdSpend={setAdSpend}
              inputError={inputError}
            />
          </div>

          <TermsAndConditions content={termsAndConditions} />
        </div>

        {inputError && <FormErrorMessage />}

        <Button
          className="mx-auto py-2 px-22 my-10"
          variant={ButtonVariant.primary}
          text={buttonText}
          size={ButtonSize.large}
          onClick={e => {
            buttonOnClick();
          }}
        />
        {loading && (
          <div className="transition duration-200 absolute top-0 w-full h-full left-0 p-8 bg-white flex items-center justify-center rounded-xl z-50">
            <Loading />
          </div>
        )}
        {submitted && (
          <div className="absolute w-full h-full left-0 top-0 p-8 bg-white flex items-center justify-center rounded-xl z-50">
            <div className="mx-auto flex flex-col gap-8 items-center">
              <GatsbyImage alt="Thank You" image={thankYouImage} />
              <h3 className="text-5xl font-bold max-w-xs text-center leading-10 align-center">
                {successText}
              </h3>
              <p className="mx-auto max-w-xl">
                Check your email shortly for the event details
              </p>
              <Button
                className="mx-auto py-2 px-22"
                variant={ButtonVariant.primary}
                text={'Resubmit'}
                size={ButtonSize.large}
                onClick={e => {
                  setSubmitted(false);
                }}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default WebinarsRegisterForm;
